import { useState } from 'react';
//import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Dialog } from 'components/ui/dialog';
import { Badge } from 'components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useBillingAddress, useCancelSubscription } from 'features/subscription/hooks/use-subscription';
import { initializeCurrentPlan } from 'features/subscription/services/subscription-service';
import { EditBillingDetails } from 'features/profile/component/modals/edit-billing-details';
import { useSubscriptionStore } from 'state/store/subscription';
import { useConfig } from 'hooks/use-config';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { PLANS } from 'constant/plan';
import { useCommonStore } from 'state/store/common/useCommonStore';

/**
 * `SubscriptionInfo` component renders the user's subscription details including
 * current plan, payment method, billing info, billing history, and subscription management.
 * Based on the Figma design for the subscription tab in the profile section.
 *
 * Translation Keys Required (to be added to 'profile' module):
 * - CURRENT_PLAN: "Current Plan"
 * - CURRENT_PLAN_DESCRIPTION: "Here's a snapshot of your current subscription"
 * - UPDATE_PLAN: "Update Plan"
 * - PAYMENT_METHOD: "Payment Method"
 * - PAYMENT_METHOD_DESCRIPTION: "Here's how you're currently paying"
 * - UPDATE: "Update"
 * - BILLING_INFO: "Billing info"
 * - BILLING_INFO_DESCRIPTION: "Here's the information that will be shown on your receipt and invoice."
 * - EDIT: "Edit"
 * - NAME: "Name"
 * - EMAIL: "Email"
 * - PHONE: "Phone"
 * - ADDRESS: "Address"
 * - CITY: "City"
 * - STATE: "State"
 * - POSTCODE: "Postcode"
 * - COUNTRY: "Country"
 * - VAT: "VAT"
 * - BILLING_HISTORY: "Billing History"
 * - BILLING_HISTORY_DESCRIPTION: "Your billing history will appear here once you make a purchase."
 * - CANCEL_SUBSCRIPTION: "Cancel Subscription"
 * - CANCEL: "Cancel"
 * - CANCEL_SUBSCRIPTION_DESCRIPTION: "Once canceled, your plan will stop and you'll lose access to all features."
 *
 * @returns {JSX.Element} The rendered subscription information sections.
 */

interface SubscriptionInfoProps {
    showSubscriptionInfo?: boolean;
}

export const SubscriptionInfo = ({ showSubscriptionInfo = true }: SubscriptionInfoProps) => {
    const { t } = useTranslation();
    //const navigate = useNavigate();
    const [isEditBillingOpen, setIsEditBillingOpen] = useState(false);
    const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
    const { configurationSettings } = useConfig();

    // Get real subscription data from store
    const { currentPlan, isLoading: isSubscriptionLoading, setCurrentPlan, setLoading, setError } = useSubscriptionStore();
    //const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
    const isOnlyOrgUser = useCommonStore((state) => state.isOnlyOrgUser);

    // Fetch billing address
    const {
        data: billingResponse,
        isLoading: isBillingLoading
    } = useBillingAddress();

    // Hook for canceling subscription
    const { mutate: cancelSubscriptionMutation, isPending: isCanceling } = useCancelSubscription({
        onSuccess: (data) => {
            if (data.isSuccess) {
                // Refresh the current plan data to update the UI
                initializeCurrentPlan(configurationSettings, setCurrentPlan, setLoading, setError);
                // Only close modal after successful cancellation
                setIsCancelConfirmOpen(false);
            }
            // Note: Modal remains open if cancellation failed (data.isSuccess is false)
            // The error will be shown via toast notification from the hook
        },
        onError: (error) => {
            console.error('Failed to cancel subscription:', error);
            // Modal remains open on error so user can retry
        }
    });

    // Helper function to format plan pricing
    const formatPlanPrice = () => {
        if (!currentPlan) return 'CHF 0.00';

        const baseItem = currentPlan.items.find(item => item.baseItem) || currentPlan.items[0];
        if (!baseItem) return 'CHF 0.00';

        const price = baseItem.planPrice || 0;
        const currency = currentPlan.currency === 'CHF' ? 'CHF' : '$';
        return `${currency}${price.toFixed(2)}`;
    };

    // Helper function to format plan period
    const formatPlanPeriod = () => {
        if (!currentPlan) return 'N/A';

        const baseItem = currentPlan.items.find(item => item.baseItem) || currentPlan.items[0];
        if (!baseItem) return 'N/A';

        const interval = baseItem.price.paymentInterval;
        const intervalUnit = baseItem.price.paymentIntervalUnit;

        // intervalUnit: 0=day, 1=week, 2=month, 3=year
        const units = ['day', 'week', 'month', 'year'];
        const unit = units[intervalUnit] || 'month';

        if (interval === 1) {
            return unit === 'day' ? 'Daily' :
                unit === 'week' ? 'Weekly' :
                    unit === 'month' ? 'Monthly' :
                        unit === 'year' ? 'Annually' : 'Monthly';
        }

        return `${interval} ${unit}s`;
    };

    /* const handleUpdatePlan = () => {
        const targetUrl = isOrgAdmin ? '/subscription?tab=business' : '/subscription';
        navigate(targetUrl, {
            state: { from: '/profile?tab=subscription' }
        });
    }; */

    const handleEditBilling = () => {
        setIsEditBillingOpen(true);
    };

    const handleCancelSubscription = () => {
        setIsCancelConfirmOpen(true);
    };

    const handleConfirmCancellation = () => {
        cancelSubscriptionMutation();
    };

    if (isSubscriptionLoading || isBillingLoading) {
        return (
            <div className="space-y-6">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
            </div>
        );
    }



    // Get billing data from API response
    const billingData = billingResponse?.data;

    return (
        <Card className="w-full border-none rounded-none shadow-none bg-transparent">
            <CardContent className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                {/* Current Plan Section */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <h3 className="text-lg sm:text-xl font-semibold text-foreground">
                            {t('CURRENT_PLAN')}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {t('CURRENT_PLAN_DESCRIPTION')}
                        </p>
                    </div>
                    <div className="flex items-center justify-between w-full">
                        <div className="flex-1">
                            <h4 className="text-sm sm:text-base font-semibold text-foreground mb-1">
                                {currentPlan?.title || currentPlan?.name || 'No Plan'}
                            </h4>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                                {formatPlanPrice()}/{formatPlanPeriod()} {currentPlan?.validUntil ? `(${t('VALID_UNTIL')} ${new Date(currentPlan.validUntil).toLocaleDateString(
                                    'de-CH', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                })}, ${new Date(currentPlan.validUntil).toLocaleTimeString(
                                    'de-CH', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                })})`
                                    : ''}
                            </p>
                        </div>
                        {/* {!isOnlyOrgUser && showSubscriptionInfo && (
                            <Button
                                onClick={handleUpdatePlan}
                                variant="ghost"
                                className="text-primary hover:bg-transparent hover:text-primary/80 font-semibold text-sm leading-6 px-3 py-1.5 rounded-md"
                            >
                                {t('UPDATE_PLAN')}
                            </Button>
                        )} */}
                    </div>
                </div>

                <Separator className="bg-border h-px" />

                {/* Billing Info Section - Only show if billing data exists */}
                {!isOnlyOrgUser && showSubscriptionInfo && billingData && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-start justify-between w-full">
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground flex-1">
                                    {t('BILLING_INFO')}
                                </h3>
                                <Button
                                    onClick={handleEditBilling}
                                    variant="ghost"
                                    className="text-primary hover:bg-transparent hover:text-primary/80 font-semibold text-xs sm:text-sm px-3 py-1.5 rounded-md"
                                >
                                    {t('EDIT')}
                                </Button>
                            </div>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                                {t('BILLING_INFO_DESCRIPTION')}
                            </p>
                        </div>
                        <div className="space-y-2 sm:space-y-3">
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('EMAIL')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.email || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('NAME')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.name || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('PHONE')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.phone || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('ADDRESS')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.address || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('CITY')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.city || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('STATE')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.state || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('POSTCODE')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.postcode || '-'}
                                </div>
                            </div>
                            <div className="flex gap-4 sm:gap-9 items-start">
                                <div className="w-[80px] sm:w-[100px] text-xs sm:text-sm font-medium text-foreground">
                                    {t('COUNTRY')}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-muted-foreground flex-1 break-words">
                                    {billingData.country || '-'}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {billingData && <Separator className="bg-border h-px" />}

                {/* Cancel Subscription Section - Only show for paid plans (excluding FREE, NOPLAN, and ENTERPRISE) and for user who is not Orguser */}
                {!isOnlyOrgUser && currentPlan && currentPlan.name !== PLANS.FREE && currentPlan.name !== PLANS.NOPLAN && currentPlan.name !== PLANS.ENTERPRISE && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-start justify-between w-full">
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground flex-1">
                                    {t('CANCEL_SUBSCRIPTION')}
                                </h3>
                                {currentPlan?.endDate ? (
                                    <Badge
                                        variant="secondary"
                                        className="text-muted-foreground bg-muted/50 text-xs sm:text-sm font-semibold px-3 py-1.5"
                                    >
                                        Canceled
                                    </Badge>
                                ) : (
                                    <Button
                                        onClick={handleCancelSubscription}
                                        variant="ghost"
                                        className="text-destructive hover:bg-transparent hover:text-destructive/80 font-semibold text-xs sm:text-sm px-3 py-1.5 rounded-md"
                                    >
                                        {t('CANCEL')}
                                    </Button>
                                )}
                            </div>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                                {currentPlan?.endDate
                                    ? `${t('SUBSCRIPTION_CANCELED_END_DATE')} ${new Date(currentPlan.endDate).toLocaleDateString('de-CH', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    })}, ${new Date(currentPlan.endDate).toLocaleTimeString(
                                        'de-CH', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                    })}`
                                    : t('CANCEL_SUBSCRIPTION_DESCRIPTION')
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* Bottom spacing */}
                <div className="h-[50px]" />
            </CardContent>

            {/* Edit Billing Dialog */}
            <Dialog open={isEditBillingOpen} onOpenChange={(open) => {
                if (!open) {
                    // Dialog is being closed - this handles the X button
                    setIsEditBillingOpen(false);
                }
            }}>
                {billingData && (
                    <EditBillingDetails
                        billingInfo={billingData}
                        onClose={() => setIsEditBillingOpen(false)}
                        isOpen={isEditBillingOpen}
                    />
                )}
            </Dialog>

            {/* Cancel Subscription Confirmation Dialog */}
            <ConfirmationModal
                open={isCancelConfirmOpen}
                onOpenChange={setIsCancelConfirmOpen}
                title="Cancel Subscription"
                description="Are you sure you want to cancel your subscription? This action will stop your plan and you'll lose access to all premium features at the end of your current billing period."
                onConfirm={handleConfirmCancellation}
                confirmText={isCanceling ? "Canceling..." : "Cancel Subscription"}
                cancelText="Keep Subscription"
                preventAutoClose={true}
            />
        </Card>
    );
};